// Contrato de los filtros de la HOME de Obras (R4a — Home con Filtros,
// 4.0.0-rc.5). Fase 0 del orquestador (context/plan_migracion_reui.md,
// Ajuste 32 y la sección «R4a — Home con Filtros»; decisiones 1–5 de Juan del
// 25/9 en BACKLOG.md, entrada «Home de Obras con el patrón "Filtros"»;
// comportamiento de referencia en el artboard del canvas
// scratchpad/canvas/project/Home.dc.html — incluido el `detectarToken`
// portado — + home.css y los 12 boards `Home-*.dc.html`; propuesta original
// en context/home_filtros_panel/sintesis.md §2). Sin JSX (regla de Fast
// Refresh): solo tipos, constantes y la interfaz `ContratoObrasFiltros`, con
// las FIRMAS (no la implementación) de las funciones puras del view-model.
//
// Lo escribe el orquestador; lo leen en paralelo:
// - R4a-A (view-model): components/obras/obras-filtros.ts (implementa
//   `ContratoObrasFiltros`) + sus tests, hooks/use-obras-filtros-persistidos.ts
//   (serializar/parsearPersistidos). Borra filtros-texto.ts y su test: la
//   detección del Enter pasa a `components/filtros/tokens.ts` (parametrizada).
// - R4a-B (UI): pages/obras.tsx, components/obras/filtros-obras-popover.tsx
//   y meta-obras.tsx (nuevos), obra-card.tsx. Borra obras-filters.tsx entero.
// - R4a-QA: escenarios-home.mjs (nuevo) + fixtures de /api/obras.
//
// Reusa SIEMPRE lo genérico de components/filtros/ (nunca se duplica acá):
// `OpcionFiltro`, `ChipFiltro`, `DimensionFaceta`, `ConteoFacetas`,
// `UnidadConteo` de tipos.ts; `contarFacetas`/`alternarValor`/`textoConteo`/
// `conteoSr` de facetas.ts (BACKLOG 25/9: «contarFacetas y alternarValor
// quedaron sin consumidor: la home los adopta, y de paso se unifica el
// conteo del Avivador» — a diferencia de avivador-filtros.ts, que cuenta las
// facetas a mano, `ContratoObrasFiltros.contarFacetas` SIEMPRE llama a la
// `contarFacetas` compartida; `alternarValor` se importa y se usa TAL CUAL en
// los `onValorChange` de `GrupoChips` — no hace falta declararla acá ni
// reimplementarla); `detectarToken`/`normalizarBusqueda` de tokens.ts para el
// Enter del buscador.
//
// Reusa de components/obras/constants.ts lo que sigue siendo el MISMO dato de
// dominio (se re-exporta más abajo para que los 3 ejecutores importen todo
// del contrato nuevo en un solo lugar): `ENC_NOMBRES`, `ESTADO_OBRA_ORDEN`,
// `SIN_ESTADO_OBRA`. `SIN_ENCARGADA` se re-exporta con un nombre nuevo
// (`SIN_CONTROL_RDT`, mismo valor: es el mismo sentinel «obra sin Control
// RDT», ahora de selección ÚNICA en vez de checkbox).
//
// Sale ENTERO con esta migración (no se toca ni se reusa): `FilterToken` /
// `FilterTokenField` (constants.ts), `DEFAULT_RDT`, `RDT_OPCIONES_FIJAS`,
// `SIN_ESTADO_RDT` («(Sin estado)», reemplazado por `SIN_ESTADO_REDES`, más
// abajo, con la etiqueta del Ajuste 16) y todo `filtros-texto.ts`
// (`detectToken`, `matchOne`, `searchHaystack`, `normText` — la home usa la
// detección PARAMETRIZADA de components/filtros/tokens.ts, no esa).
//
// NUNCA importar avivador-filtros.ts, avivador-tipos.ts ni avivador-vm.ts (la
// v1): son de otro dominio. Se puede mirar por analogía (el patrón de este
// archivo es deliberadamente paralelo a avivador-tipos.ts), nunca importar
// código de ahí.
import type { ChipFiltro, ConteoFacetas, UnidadConteo } from '@/components/filtros/tipos';
import { ENC_NOMBRES, ESTADO_OBRA_ORDEN, SIN_ENCARGADA, SIN_ESTADO_OBRA } from '@/components/obras/constants';
import type { ObraCard } from '@/types/api';

export { ENC_NOMBRES, ESTADO_OBRA_ORDEN, SIN_ESTADO_OBRA };

// ---------------------------------------------------------------------------
// Universo y sentinels de cada sección (BACKLOG 25/9, decisiones 1–5;
// artboard Home.dc.html, síntesis §2 «Popover» y «Modo archivo»)
// ---------------------------------------------------------------------------

/** ¿La obra entra en el universo «en curso» (switch Archivadas apagado)?
 *  Cualquier `estadoRdt` que NO sea 'Archivado' ni 'No Aplica' — INCLUIDA una
 *  obra con `estadoRdt` null/vacío (cae en la pseudo-opción
 *  `SIN_ESTADO_REDES`, pero sigue «en curso»; mismo criterio que `enBase` de
 *  hoy con 'No Aplica', obras.tsx:102-107). Es un PREDICADO, no una lista
 *  fija de 3 estados — así una obra sin ninguno de los 3 estados conocidos
 *  tampoco desaparece (artboard `enCurso`, Home.dc.html:250). */
export function enCursoDeRedes(o: Pick<ObraCard, 'estadoRdt'>): boolean {
  return o.estadoRdt !== 'Archivado' && o.estadoRdt !== 'No Aplica';
}

/** Sentinel de Control RDT «Todo el equipo»: sin filtro de persona. Es el
 *  default y la home SIEMPRE abre acá (decisión 2, BACKLOG 25/9 — a
 *  diferencia del Avivador, que abre filtrado por la persona de quien
 *  entra: «la home es el directorio del equipo»). Vacío, como en el artboard
 *  (`opsCtrl = [['', 'Todo el equipo']]`, Home.dc.html:444) — DISTINTO del
 *  `TODO_EL_EQUIPO = 'Todas'` del Avivador (avivador-tipos.ts:127): son dos
 *  dominios, cada uno con su propio sentinel; no compartir el valor. */
export const TODO_EL_EQUIPO_OBRAS = '';

/** Pseudo-opción «Sin Control RDT»: MISMO sentinel que `SIN_ENCARGADA` de
 *  components/obras/constants.ts (una obra sin `encargada` cargada), ahora
 *  en un `RadioGroup` de selección única en vez del checkbox de hoy. Solo se
 *  OFRECE con `archivadas` prendido (decisión 3, BACKLOG 25/9: son 138
 *  obras, todas archivadas — sin el switch no hay ninguna que mostrar, así
 *  que ofrecerla sería un filtro señuelo, mismo criterio que ya usa
 *  `computeRdtOptions` hoy para 'Central'). */
export const SIN_CONTROL_RDT = SIN_ENCARGADA;

/** Los 3 estados de redes «en curso», en el ORDEN del popover y de la fila
 *  «Mostrando N en curso de M obras» (artboard `REDES_CURSO`,
 *  Home.dc.html:238). Se ofrecen SIEMPRE, con `archivadas` prendido o no. */
export const ESTADOS_REDES_EN_CURSO: readonly string[] = ['Activo', 'Esperando Definitiva', 'A cargo de la UTE'];

/** Los 2 que solo se OFRECEN con `archivadas` prendido (decisión 3; artboard
 *  `REDES_ARCH`, Home.dc.html:239 — No Aplica antes que Archivado, mismo
 *  orden que la tabla de facetas de la síntesis §2 «No Aplica 7 · Archivado
 *  166»). Elegir uno de estos con `archivadas` apagado no es alcanzable por
 *  la UI; si igual llegara así desde sessionStorage, `sanearFiltrosObras` lo
 *  descarta (ver más abajo). */
export const ESTADOS_REDES_SOLO_ARCHIVO: readonly string[] = ['No Aplica', 'Archivado'];

/** Pseudo-opción «(Sin estado de redes)»: se OFRECE solo si hay al menos una
 *  obra del universo vigente (en curso, o las 240 con `archivadas`) con
 *  `estadoRdt` vacío — hoy 0 casos; si aparece cae dentro de «en curso»
 *  (`enCursoDeRedes` de arriba, síntesis §2, nota). Reemplaza a
 *  `SIN_ESTADO_RDT = '(Sin estado)'` del modelo viejo (constants.ts): el
 *  texto sube a «de redes» por el Ajuste 16 («Estado de obra:» / «Estado de
 *  redes:», nunca «Estado:» a secas). `SIN_ESTADO_RDT` se retira cuando
 *  `obras-filters.tsx` y `filtros-texto.ts` se borren (R4a-B/A). */
export const SIN_ESTADO_REDES = '(Sin estado de redes)';

/** Etiquetas de categoría del Ajuste 16 (BACKLOG 24/9, decisión 5(b) — «la
 *  recomendación gana»): SIN los dos puntos (los pone el chip / el título de
 *  la sección). Nunca «Estado:» a secas: es ambiguo entre obra y redes. */
export const ETIQUETA_ESTADO_OBRA = 'Estado de obra';
export const ETIQUETA_ESTADO_REDES = 'Estado de redes';
export const ETIQUETA_CONTROL_RDT = 'Control RDT';

/** Sustantivo del conteo sr-only de las 3 secciones del popover (`conteoSr`,
 *  components/filtros/facetas.ts): «21 obras», «1 obra». */
export const UNIDAD_OBRAS: UnidadConteo = { singular: 'obra', plural: 'obras' };

/** Ids de las 3 dimensiones del conteo facetado (`DimensionFaceta.id`,
 *  `ConteoFacetas` de components/filtros/tipos.ts): una clave estable que
 *  usan a la vez el view-model (`contarFacetas`, más abajo) y la UI
 *  (`facetas[DIM_ESTADO_REDES][valor]`), así no divergen por un typo. */
export const DIM_ESTADO_REDES = 'estadosRedes';
export const DIM_CONTROL_RDT = 'controlRdt';
export const DIM_ESTADO_OBRA = 'estadosObra';

// ---------------------------------------------------------------------------
// Tokens del Enter (los que SIGUEN siendo token: Obra, Cliente, Coordinación
// y texto libre — Control RDT, Estado de obra y Estado de redes se VUELCAN a
// su sección del popover, ver `ResultadoEnterObras` más abajo)
// ---------------------------------------------------------------------------

/** Campo de un token. `'obra'` sale de un CC exacto (con alias sin «` UTE`»)
 *  o de un nombre de obra único; en los dos casos `value` Y `label` son el
 *  CC (mismo criterio que el Avivador: «metrobus» → «Obra: 1426», el label
 *  NUNCA es la denominación). `'text'` = texto libre, sin categoría. */
export type TokenObraField = 'obra' | 'cliente' | 'coordinador' | 'text';

/** Etiqueta visible de la categoría del chip (sin los dos puntos). `''` =
 *  texto libre (el chip no antepone nada). */
export type TokenObraCat = '' | 'Obra' | 'Cliente' | 'Coordinación';

/** `cat` que corresponde a cada `field` (el único válido; mismo patrón que
 *  `CAT_POR_CAMPO` del Avivador, avivador-tipos.ts). */
export const CAT_POR_CAMPO_OBRAS: Readonly<Record<TokenObraField, TokenObraCat>> = {
  obra: 'Obra',
  cliente: 'Cliente',
  coordinador: 'Coordinación',
  text: '',
};

export interface TokenObra {
  field: TokenObraField;
  /** `obra` → cc exacto; `cliente` / `coordinador` → el valor crudo del dato;
   *  `text` → `normalizarBusqueda(texto)` (components/filtros/tokens.ts). */
  value: string;
  /** Siempre `CAT_POR_CAMPO_OBRAS[field]`. */
  cat: TokenObraCat;
  /** Texto visible del chip: para `field: 'obra'`, SIEMPRE el cc (aunque se
   *  haya detectado por el nombre de la obra); para el resto, el valor tal
   *  cual se tipeó/detectó. */
  label: string;
}

// ---------------------------------------------------------------------------
// FiltrosObras — el estado persistido completo (T3b Ajuste #16; BACKLOG 25/9)
// ---------------------------------------------------------------------------

/**
 * Los filtros de la home, PERSISTIDOS enteros (mismo criterio que
 * `FiltrosCola` del Avivador — `q` incluido). `archivadas` vive ACÁ ADENTRO
 * del contrato aunque su control esté FUERA del popover, en la fila
 * `[Buscador] [Filtros ·N] [Archivadas]` (decisión 1: el switch no es una
 * sección del popover, pero sí un filtro que se persiste igual que hoy,
 * obras.tsx:353).
 *
 * Regla general de toda sección múltiple (Estado de redes, Estado de obra):
 * `[]` = NINGUNA elegida = TODAS las del universo vigente (síntesis §2,
 * regla compartida por las 3 secciones — a diferencia del modelo viejo,
 * donde `[]` en un multiselect de default-todo-tildado significaba «ninguna
 * obra pasa»).
 */
export interface FiltrosObras {
  /** Texto del buscador tal cual lo tipea la persona (debounce de 250 ms en
   *  la página, MVP §5.1, sin cambios; el texto YA normalizado y con
   *  debounce que usa `ContratoObrasFiltros.filtrar`/`contarFacetas` es
   *  estado de componente, no de este contrato — mismo patrón que
   *  `FiltrosCola.q` + el `qNorm` aparte del Avivador). */
  q: string;
  /** Tokens agregados por Enter, en el orden en que se agregaron. */
  tokens: readonly TokenObra[];
  /** Estado(s) de redes elegidos (selección MÚLTIPLE, `ToggleGroup`, como
   *  Ramas en el Avivador). Valores de `ESTADOS_REDES_EN_CURSO` siempre; de
   *  `ESTADOS_REDES_SOLO_ARCHIVO` y `SIN_ESTADO_REDES` solo mientras sean
   *  válidos (`sanearFiltrosObras` los descarta si no). */
  estadosRedes: readonly string[];
  /** Control RDT de selección ÚNICA (`RadioGroup`, decisión 2). Default y
   *  apertura de la home: `TODO_EL_EQUIPO_OBRAS`. `SIN_CONTROL_RDT` solo con
   *  `archivadas`. Puede ser un código de `ENC_NOMBRES` o uno presente en los
   *  datos que no esté en el mapa (mismo criterio que `computeEncargadaOptions`
   *  de hoy, obras.tsx:62-70 — un código nuevo no tiene que quedar invisible). */
  controlRdt: string;
  /** Estado(s) de obra elegidos (selección múltiple). `SIN_ESTADO_OBRA` si
   *  corresponde, con cualquier valor de `archivadas` (no depende del
   *  switch, a diferencia de Estado de redes y Control RDT). */
  estadosObra: readonly string[];
  /** Switch «Archivadas», AFUERA del popover (decisión 1). Al prenderlo se
   *  suman opciones (nada se descarta); al apagarlo, `sanearFiltrosObras`
   *  descarta lo que quedó fuera del universo. */
  archivadas: boolean;
}

// ---------------------------------------------------------------------------
// Chips activos (meta-row) — orden y claves (BACKLOG 25/9, decisión de la
// recorrida: «orden de chips: tokens → Estado de redes → Control RDT →
// Estado de obra»)
// ---------------------------------------------------------------------------

/** Clave estable de un chip (`ChipFiltro.key`; `data-filtro-chip`, regla de
 *  foco de `MetaFiltros`). `tok:N` por índice del token; `redes:V` / `eo:V`
 *  por VALOR (no por índice: un chip de sección múltiple no tiene por qué
 *  conservar posición al reordenarse); `ctrl` es única (selección única). */
export type ChipObraKey = `tok:${number}` | `redes:${string}` | 'ctrl' | `eo:${string}`;

/** Un chip activo de la home; `cat` usa `ETIQUETA_ESTADO_REDES` /
 *  `ETIQUETA_CONTROL_RDT` / `ETIQUETA_ESTADO_OBRA` de arriba (Ajuste 16) o
 *  `TokenObraCat` para un token; `null` en un token de texto libre. */
export type ChipObra = ChipFiltro<ChipObraKey>;

// ---------------------------------------------------------------------------
// Enter del buscador (síntesis §2 «Buscador»; BACKLOG 25/9: «Estado de redes
// entra en el Enter después de Estado de obra»; artboard `detectar`,
// Home.dc.html:308-336)
// ---------------------------------------------------------------------------

/**
 * Categorías del Enter, EN ESTE ORDEN (`components/filtros/tokens.ts`,
 * `detectarToken`):
 * 1. `obraCc` — coincidencia 'exacta'; candidatos: TODAS las obras (las 240,
 *    sin importar `archivadas` — un CC es inequívoco y, si la obra está
 *    archivada, el vacío ofrece «Ver con Archivadas»), `{ valor: cc,
 *    etiqueta: cc, alias: cc termina en ' UTE' ? [cc sin ' UTE'] : [] }`.
 * 2. `cliente` — coincidencia 'inicio-de-palabra' (juez #1 de T3b: «vera» no
 *    es «Privera S.A.»); candidatos del `universoVisible` (BACKLOG 25/9:
 *    «los candidatos de Cliente y Coordinación del Enter salen del universo
 *    visible» — con el switch apagado, «vera» no puede caer en un cliente
 *    que solo existe archivado).
 * 3. `coordinador` — coincidencia default 'exacta-o-parcial'; candidatos del
 *    `universoVisible`, igual que cliente.
 * 4. `controlRdt` — candidatos: los códigos de `ENC_NOMBRES` + los presentes
 *    en `obras` que no estén en el mapa (mismo criterio que
 *    `computeEncargadaOptions`); SIN gating por `archivadas` (el código de
 *    una persona no depende del switch). VUELCA: reemplaza `controlRdt`.
 * 5. `estadoObra` — candidatos: `ESTADO_OBRA_ORDEN` completo (fijo, no
 *    depende de qué esté presente ni de `archivadas`). VUELCA: se agrega a
 *    `estadosObra` si no estaba.
 * 6. `estadoRedes` — candidatos: `ESTADOS_REDES_EN_CURSO`, más
 *    `ESTADOS_REDES_SOLO_ARCHIVO` SOLO con `archivadas` prendido (decisión de
 *    la recorrida: «Archivado/No Aplica solo con el switch prendido»).
 *    VUELCA: se agrega a `estadosRedes` si no estaba.
 * 7. `obraNombre` — coincidencia default 'exacta-o-parcial'; candidatos:
 *    TODAS las obras con denominación, `{ valor: cc, etiqueta: denominación
 *    }` → el TOKEN resultante usa cc como `value` Y como `label` (nunca la
 *    denominación: «metrobus» → «Obra: 1426»).
 *
 * Nada → texto libre: `{ kind: 'token', token: { field: 'text', cat: '',
 * value: normalizarBusqueda(texto), label: texto.trim() } }`. Texto vacío
 * (solo espacios, o que normaliza a nada) → `{ kind: 'vacio' }`.
 */
export type CategoriaEnterObras =
  | 'obraCc'
  | 'cliente'
  | 'coordinador'
  | 'controlRdt'
  | 'estadoObra'
  | 'estadoRedes'
  | 'obraNombre';

/** Lo que devuelve `ContratoObrasFiltros.detectarEnter`. Los 3 `kind` que NO
 *  son `'token'` VUELCAN al popover — no generan chip de token (Ajuste #16). */
export type ResultadoEnterObras =
  | { kind: 'vacio' }
  | { kind: 'token'; token: TokenObra }
  | { kind: 'controlRdt'; codigo: string }
  | { kind: 'estadoObra'; valor: string }
  | { kind: 'estadoRedes'; valor: string };

// ---------------------------------------------------------------------------
// Meta-row: variante del conteo (síntesis §2 «Meta-row y chips»)
// ---------------------------------------------------------------------------

/** Cuál de las 2 versiones del conteo mostrar. Depende SOLO de `archivadas`
 *  (los 3 ejemplos de la síntesis: apagado → 'en-curso'; prendido, CON o SIN
 *  Estado de redes elegido → 'de-total' sin «en curso» — con el switch
 *  prendido ya no hay una partición limpia que nombrar). La UI arma el texto
 *  con los números en negrita:
 *  - `'en-curso'`: «Mostrando **N** en curso de **M** obras».
 *  - `'de-total'`: «**N** de **M** obras».
 *  `N` = `filtrar(...).length` (con los filtros vigentes); `M` =
 *  `obras.length` (el universo total, sin acotar por nada — las 240). */
export type VarianteConteoObras = 'en-curso' | 'de-total';

// ---------------------------------------------------------------------------
// Persistencia por pestaña (Juan, 21/9: sessionStorage, no URL)
// ---------------------------------------------------------------------------
// plan_migracion_reui.md, sección «R4a — Home con Filtros»: «Persistencia por
// pestaña con una clave v2 (lo viejo en sessionStorage es inocuo)». Esa
// numeración es la del CONTRATO persistido, no la del string de la clave: 1
// fue el shape de destildados (`FiltrosObrasPersistidos`, v1 a v3 de
// use-obras-filtros-persistidos.ts — 3 multiselects con "todo tildado" por
// default); 2 es ESTE, con los ELEGIDOS y Control RDT de selección única
// (mismo criterio que las 4 lentes del panel — sintesis.md, lente-ia.md,
// lente-uso.md, lente-critico.md — que coinciden en el string LITERAL: la
// 4ª versión del NOMBRE de la clave, porque v1 a v3 ya están usadas). Lo
// persistido con esas 3 versiones simplemente no matchea `parsearPersistidos`
// (el `v` de abajo no matchea `OBRAS_FILTROS_VERSION`) y se ignora sin
// migrar — inocuo, sessionStorage es por pestaña.

/** Generación del CONTRATO persistido (embebida en el JSON como `v`, NO en el
 *  string de la clave): la 2ª, después del shape de destildados. La usa
 *  `parsearPersistidos` para rechazar de un JSON que por casualidad tuviera
 *  los mismos NOMBRES de campo pero otro significado. */
export const OBRAS_FILTROS_VERSION = 2;

/** Clave de sessionStorage, por pestaña. 4ª del NOMBRE (v1 → v2 → v3 hoy en
 *  el código; v3 = `use-obras-filtros-persistidos.ts:15`, «etiquetas
 *  neutras», 0.18.2) — la nueva versión del contrato (`OBRAS_FILTROS_VERSION`
 *  = 2) usa la 4ª. R4a-A borra el archivo viejo (`use-obras-filtros-
 *  persistidos.ts`) y lo reescribe sobre esta clave; nunca migra v1-v3. */
export const OBRAS_FILTROS_STORAGE_KEY = 'redeapp.obras.filtros.v4';

// ---------------------------------------------------------------------------
// ContratoObrasFiltros — firmas del view-model (implementa R4a-A en
// components/obras/obras-filtros.ts; SIN implementación acá)
// ---------------------------------------------------------------------------

/**
 * Todas las funciones puras de la home. `obras-filtros.ts` (R4a-A) exporta
 * cada miembro como función suelta (mismo patrón que avivador-filtros.ts: NO
 * hace falta instanciar un objeto que cumpla el tipo, la interfaz es el
 * contrato de FIRMAS que `obras-filtros.test.ts` verifica función por
 * función). `pages/obras.tsx` y `hooks/use-obras-filtros-persistidos.ts`
 * (R4a-B) consumen esas funciones.
 */
export interface ContratoObrasFiltros {
  /** Texto normalizado (`normalizarBusqueda`, components/filtros/tokens.ts)
   *  donde busca el buscador en vivo y el token `'text'`: denominación,
   *  cliente, cc, coordinador y `codigoObra` (mismo campo que
   *  `searchHaystack` de hoy, filtros-texto.ts:61-65 — PORTADO acá, ese
   *  archivo se borra). Cacheado por obra igual que `haystack` del
   *  Avivador (no hace falta que la firma lo exprese: es un detalle de
   *  implementación de `obras-filtros.ts`). */
  haystack(o: ObraCard): string;

  /** El universo del Enter para Cliente y Coordinación (BACKLOG 25/9: «los
   *  candidatos de Cliente y Coordinación del Enter salen del universo
   *  visible»): `obras` acotadas SOLO por `archivadas` (en curso via
   *  `enCursoDeRedes`, o las 240) — SIN los demás filtros del popover ni el
   *  texto en vivo (mismo criterio que `visibles` del artboard,
   *  Home.dc.html:319). La usan `detectarEnter` (cliente/coordinador) y
   *  puede reusarla la UI para poblar el popover si hiciera falta. */
  universoVisible(obras: readonly ObraCard[], archivadas: boolean): readonly ObraCard[];

  /**
   * Todo el filtrado de la home (reemplaza `filterObras`, obras.tsx:137-155):
   * - Universo: `archivadas ? obras : obras.filter(enCursoDeRedes)`.
   * - ∩ tokens (AND entre todos: `obra` → `cc === value`; `cliente` /
   *   `coordinador` → el campo correspondiente `=== value`; `text` →
   *   `haystack(o).includes(value)`).
   * - ∩ `estadosRedes` (`[]` = todas; una obra sin `estadoRdt` matchea
   *   `SIN_ESTADO_REDES`), `controlRdt` (`TODO_EL_EQUIPO_OBRAS` = sin
   *   filtro; una obra sin `encargada` matchea `SIN_CONTROL_RDT`),
   *   `estadosObra` (`[]` = todas; sin `estado` matchea `SIN_ESTADO_OBRA`).
   * - ∩ texto en vivo: `qNorm` YA normalizado (`normalizarBusqueda`) y CON
   *   DEBOUNCE de 250 ms (estado de la página, no de `FiltrosObras.q`) —
   *   mismo criterio que el Avivador (juez #9 de T3b): nunca filtrar contra
   *   `f.q` crudo, siempre contra el `qNorm` debounced que recibe aparte.
   * Devuelve la lista en el orden FIJO de la home: cc descendente
   * (`ccSortKey`, obras.tsx:88-91/153, SIN CAMBIOS — MVP_HIGHLIGHTS.md:153;
   * la migración no toca el orden, solo cómo se llega a la lista).
   */
  filtrar(obras: readonly ObraCard[], f: FiltrosObras, qNorm: string): readonly ObraCard[];

  /**
   * Conteo facetado de las 3 secciones del popover, SIEMPRE con
   * `contarFacetas` de components/filtros/facetas.ts (nunca a mano — a
   * diferencia de `avivador-filtros.ts`) sobre 3 `DimensionFaceta<ObraCard>`
   * con id `DIM_ESTADO_REDES` / `DIM_CONTROL_RDT` / `DIM_ESTADO_OBRA`. Cada
   * dimensión evalúa sobre el universo YA acotado por `archivadas` ∩ tokens
   * ∩ texto en vivo (NUNCA sobre `obras` sin acotar: con el switch apagado,
   * los conteos de Estado de redes no tienen que sumar las obras archivadas
   * — mismo universo que usa `filtrar`, antes de aplicar `estadosRedes` /
   * `controlRdt` / `estadosObra`). El `pasa` de cada dimensión son las
   * OTRAS 2 secciones del popover (nunca la propia, nunca el universo, que
   * ya quedó afuera antes de facetar).
   */
  contarFacetas(obras: readonly ObraCard[], f: FiltrosObras, qNorm: string): ConteoFacetas;

  /**
   * Clasifica el texto del Enter con `detectarToken` de
   * components/filtros/tokens.ts y las categorías/orden de
   * `CategoriaEnterObras` (documentado ahí arriba en detalle). `obras` es
   * SIEMPRE la población completa (las 240): la función deriva
   * `universoVisible` internamente para `cliente`/`coordinador`, no hace
   * falta pre-filtrarla antes de llamarla.
   */
  detectarEnter(texto: string, obras: readonly ObraCard[], archivadas: boolean): ResultadoEnterObras;

  /**
   * Aplica el resultado del Enter (inmutable, siempre un `FiltrosObras`
   * nuevo): `'vacio'` → copia igual; `'token'` → se agrega al final de
   * `tokens` si no hay uno con el mismo `field` + `value` (no se duplica);
   * `'controlRdt'` → reemplaza `controlRdt`; `'estadoObra'` / `'estadoRedes'`
   * → se agrega a la selección múltiple correspondiente si no estaba. En
   * TODOS salvo `'vacio'`, `q` vuelve a `''` (el chip o la selección
   * reemplaza al texto tipeado). `archivadas` nunca cambia acá.
   */
  aplicarEnter(f: FiltrosObras, r: ResultadoEnterObras): FiltrosObras;

  /**
   * Chips activos, en ESTE ORDEN (BACKLOG 25/9, recorrida del 25/9): tokens
   * (orden de alta) → Estado de redes (orden de `ESTADOS_REDES_EN_CURSO` +
   * `ESTADOS_REDES_SOLO_ARCHIVO` + `SIN_ESTADO_REDES`, NUNCA el orden en que
   * se eligieron) → Control RDT (un chip si no es `TODO_EL_EQUIPO_OBRAS`) →
   * Estado de obra (orden de `ESTADO_OBRA_ORDEN` + `SIN_ESTADO_OBRA`). Cada
   * chip usa las etiquetas del Ajuste 16 (`ETIQUETA_ESTADO_REDES` /
   * `ETIQUETA_CONTROL_RDT` / `ETIQUETA_ESTADO_OBRA`, o `TokenObraCat` para un
   * token) — nunca «Estado:» a secas. `key` sigue `ChipObraKey`.
   */
  chipsActivos(f: FiltrosObras): readonly ChipObra[];

  /**
   * Filtros sin ese chip (inmutable; mismo patrón que `quitarChip` del
   * Avivador): `tok:N` saca el token en el índice N; `redes:V` / `eo:V`
   * sacan V de su array; `'ctrl'` vuelve `controlRdt` a
   * `TODO_EL_EQUIPO_OBRAS`. `archivadas` NUNCA cambia acá — no hay chip para
   * el switch, vive en la fila, no en la meta-row (decisión 1). Una key que
   * no aplica devuelve una copia igual.
   */
  quitarChip(f: FiltrosObras, key: ChipObraKey): FiltrosObras;

  /**
   * «Limpiar» del popover y «Limpiar todo» de la meta-row (UN SOLO VERBO,
   * síntesis §2 «Limpiar y Restaurar»; se retira «Restablecer filtros»):
   * `q: ''`, `tokens: []`, `estadosRedes: []`, `controlRdt:
   * TODO_EL_EQUIPO_OBRAS`, `estadosObra: []`. **NUNCA TOCA `archivadas`** —
   * decisión 1 de Juan (25/9): «el switch Archivadas afuera y "Limpiar" no
   * lo apaga» (cambia `obras.tsx:475-479` de hoy, que hoy SÍ apaga el
   * switch). Con `archivadas` prendido, no hace falta sanear nada más: las
   * 3 secciones ya vuelven a «ninguna elegida» / `TODO_EL_EQUIPO_OBRAS`.
   */
  limpiarFiltros(archivadas: boolean): FiltrosObras;

  /**
   * Reconciliación contra el universo del modo (síntesis §2 «Modo archivo»,
   * «Al apagarlo se descartan las elecciones que quedaron fuera del
   * universo»; artboard Home.dc.html:617): si `f.archivadas` es `false`, saca
   * de `estadosRedes` cualquier valor de `ESTADOS_REDES_SOLO_ARCHIVO` y, si
   * `controlRdt === SIN_CONTROL_RDT`, lo vuelve a `TODO_EL_EQUIPO_OBRAS`.
   * `estadosObra` y los tokens NUNCA se tocan (no dependen del switch). Con
   * `f.archivadas === true` devuelve una copia igual (solo se SUMAN
   * opciones al prender, nada que sanear). La llama el toggle de la fila al
   * apagar Archivadas Y `parsearPersistidos` (después de leer, por si
   * sessionStorage quedó con `archivadas: false` pero una sección con un
   * valor solo-archivo — no debería pasar por la UI, pero es defensivo,
   * mismo criterio que el resto del parseo).
   */
  sanearFiltrosObras(f: FiltrosObras): FiltrosObras;

  /** Serializa TODO `FiltrosObras` (incluido `q`) más `OBRAS_FILTROS_VERSION`
   *  embebida como `v` en el JSON (mismo patrón que `serializarFiltros` del
   *  Avivador, adaptado: acá la versión va ADENTRO del JSON, no en el string
   *  de la clave — ver la nota de «Persistencia» más arriba).
   *  `parsearPersistidos(serializar(f))` da un `FiltrosObras`
   *  estructuralmente igual a `f` (los arrays son `readonly`, comparar por
   *  valor, no por referencia). */
  serializar(f: FiltrosObras): string;

  /**
   * Parseo defensivo de lo leído de `sessionStorage.getItem(
   * OBRAS_FILTROS_STORAGE_KEY)` (todo o nada): `null` ante `raw == null`,
   * JSON inválido, `v !== OBRAS_FILTROS_VERSION`, o cualquier campo con otra
   * forma (`q`/`controlRdt`: string; `tokens`: array de `TokenObra` válidos
   * — `cat` tiene que ser `CAT_POR_CAMPO_OBRAS[field]`; `estadosRedes` /
   * `estadosObra`: array de string sin repetidos; `archivadas`: boolean).
   * Ante éxito, SIEMPRE pasa el resultado por `sanearFiltrosObras` antes de
   * devolverlo (defensivo: un JSON tocado a mano no debe poder dejar un
   * valor solo-archivo con `archivadas: false`). Quien llama, ante `null`,
   * arranca con `limpiarFiltros(false)`.
   */
  parsearPersistidos(raw: string | null): FiltrosObras | null;

  /** Ver `VarianteConteoObras` arriba: qué versión del conteo mostrar según
   *  `archivadas`. Depende solo de ese campo, no de `f` entero — se recibe
   *  así para que la firma sea explícita sobre qué necesita en runtime. */
  varianteConteo(archivadas: boolean): VarianteConteoObras;

  /**
   * Badge de «Filtros» (Ajuste 16, BACKLOG 25/9 — decisión 5(a): «que
   * cuente los tokens, como dice el spec»; regla de la recorrida: «el switch
   * no suma; los tokens sí»):
   * `estadosRedes.length + (controlRdt !== TODO_EL_EQUIPO_OBRAS ? 1 : 0) +
   * estadosObra.length + tokens.length`. `archivadas` NUNCA suma (vive
   * afuera del popover, decisión 1). Los tokens SÍ suman — a diferencia del
   * switch — por eso el popover muestra además la línea «+ N de la
   * búsqueda» (con este mismo `tokens.length`) para que un badge con nada
   * elegido ADENTRO del popover no parezca un error.
   */
  contarFiltros(f: FiltrosObras): number;
}
